import {ReactNode} from 'react'
import styles from './styles.module.scss'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../../../state/store'
import {getTimeDifferenceLabel} from '../../../../../../helpers/time-dates'
import {AlertPageItem} from '../../../../../../state/V2/openapi'

const PodAlerts = ({onSelect}: {onSelect: (id: number) => void}) => {
  const alerts = useSelector((state: RootState) => state.alerts.list.data.results)

  const intl = useIntl()

  return (
    <div className={styles.podalerts}>
      {alerts?.map(({type, participant, createdAt}: AlertPageItem, index: number) => {
        if (!participant || !type) return null
        let title = intl.formatMessage({id: `ALERT.${type}`})
        let icon: ReactNode | null = null
        switch (type) {
          case 'HIGH_HEART_RATE':
          case 'MEDIUM_HEART_RATE':
          case 'LOW_HEART_RATE':
            icon = <HeartIcon />
            break
          case 'BAND_TAMPER':
            icon = <BrokenLinkIcon />
            break
          case 'LOW_BATTERY_LEVEL':
            icon = <LowBatteryIcon />
            break
          case 'BAND_OFFLINE':
            icon = <BandOfflineIcon />
            break
          case 'HEART_RATE_OFFLINE':
            icon = <HeartRateOfflineIcon />
            break
        }

        const currentTime = new Date()
        const createdAtDate = createdAt ? new Date(createdAt) : new Date()
        const timeDifferenceMs = currentTime.getTime() - createdAtDate.getTime()

        const timeLabel = getTimeDifferenceLabel(timeDifferenceMs, intl)
        const timeValue =
          timeDifferenceMs < 60 * 60 * 1000
            ? Math.round(timeDifferenceMs / (1000 * 60))
            : Math.round(timeDifferenceMs / (1000 * 60 * 60))

        return (
          <div
            key={type + participant.id + index}
            className={styles.alert}
            onClick={() => onSelect(participant.id)}
          >
            <div className={styles.icon}>{icon}</div>

            <div className={styles.content}>
              <div className={styles.title}>
                <div className={styles.type}>{title}</div>
                <div className={styles.time}>
                  {timeValue} {timeLabel}
                </div>
              </div>

              <div className={styles.inmateId}>
                {intl.formatMessage({
                  id: 'LIVETRACK.SIDEBAR.INMATE_ID',
                })}
                : {participant.id}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const HeartIcon = () => {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M13 4.5C13 2.84315 11.6009 1.5 9.875 1.5C8.58459 1.5 7.47685 2.25085 7 3.32228C6.52315 2.25085 5.41541 1.5 4.125 1.5C2.39911 1.5 1 2.84315 1 4.5C1 9.31371 7 12.5 7 12.5C7 12.5 13 9.31371 13 4.5Z'
        stroke='#FDFDFC'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

const BrokenLinkIcon = () => {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M9.86135 2.13866L9.86143 2.13875C10.2228 2.4989 10.4264 2.98776 10.4275 3.49793C10.4286 4.00778 10.2275 4.49724 9.86814 4.85892L9.10573 5.58636L9.10564 5.58627L9.10271 5.58941C9.00261 5.6965 8.9472 5.83778 8.94779 5.98436C8.94838 6.13095 9.00494 6.27177 9.10589 6.37805C9.20685 6.48433 9.34459 6.54804 9.49095 6.55616C9.63731 6.56428 9.78124 6.51619 9.89333 6.42173L9.89342 6.42183L9.89676 6.41865L10.6641 5.6869C10.6682 5.68319 10.6721 5.67928 10.6758 5.6752C11.2441 5.09706 11.561 4.31791 11.5577 3.50725C11.5544 2.69606 11.2307 1.91903 10.6571 1.34542C10.0835 0.771815 9.30644 0.448104 8.49524 0.444801C7.68459 0.4415 6.90544 0.758449 6.3273 1.32665C6.32321 1.33038 6.31931 1.33431 6.31559 1.33841L5.58385 2.10574L5.58375 2.10564L5.58077 2.10917C5.48631 2.22126 5.43822 2.36519 5.44634 2.51155C5.45446 2.65791 5.51817 2.79565 5.62445 2.89661C5.73073 2.99756 5.87155 3.05412 6.01813 3.05471C6.16472 3.0553 6.30599 2.99989 6.41309 2.89979L6.41317 2.89988L6.41614 2.89677L7.14357 2.13437C7.50483 1.77546 7.9936 1.57431 8.50286 1.57499C9.01245 1.57566 9.50098 1.77837 9.86135 2.13866ZM1.59166 8.49746C1.58797 7.99228 1.78302 7.50591 2.1347 7.14326L2.89677 6.41614L2.89686 6.41623L2.89979 6.41309C2.99989 6.30599 3.0553 6.16472 3.05471 6.01813C3.05412 5.87155 2.99756 5.73073 2.89661 5.62445C2.79565 5.51817 2.65791 5.45446 2.51155 5.44634C2.36519 5.43822 2.22126 5.48631 2.10917 5.58077L2.10908 5.58067L2.10583 5.58376L1.33596 6.31554C1.33184 6.31927 1.3279 6.3232 1.32416 6.3273C0.75595 6.90544 0.439 7.68459 0.442301 8.49524C0.445604 9.30644 0.769315 10.0835 1.34292 10.6571C1.91653 11.2307 2.69356 11.5544 3.50475 11.5577C4.31541 11.561 5.09456 11.2441 5.6727 10.6758C5.67678 10.6721 5.68069 10.6682 5.6844 10.6641L6.41471 9.89827C6.47122 9.84504 6.51637 9.78092 6.54743 9.70976C6.5788 9.63792 6.59514 9.56042 6.59545 9.48204C6.59577 9.40365 6.58006 9.32603 6.54928 9.25393C6.5185 9.18184 6.4733 9.11681 6.41647 9.06282C6.35964 9.00883 6.29237 8.96704 6.21879 8.94C6.14521 8.91296 6.06688 8.90125 5.98862 8.90559C5.91035 8.90994 5.8338 8.93023 5.76366 8.96524C5.74631 8.9739 5.72945 8.98341 5.71312 8.99374V8.96775L5.58386 9.10323L4.85674 9.8653C4.49409 10.217 4.00772 10.412 3.50254 10.4083C2.99688 10.4046 2.51299 10.2021 2.15543 9.84457C1.79787 9.48701 1.59535 9.00312 1.59166 8.49746ZM11 7.425H9.5C9.3475 7.425 9.20125 7.48558 9.09341 7.59341C8.98558 7.70125 8.925 7.8475 8.925 8C8.925 8.1525 8.98558 8.29875 9.09341 8.40659C9.20125 8.51442 9.3475 8.575 9.5 8.575H11C11.1525 8.575 11.2988 8.51442 11.4066 8.40659C11.5144 8.29875 11.575 8.1525 11.575 8C11.575 7.8475 11.5144 7.70125 11.4066 7.59341C11.2988 7.48558 11.1525 7.425 11 7.425ZM1 4.575H2.5C2.6525 4.575 2.79875 4.51442 2.90659 4.40659C3.01442 4.29875 3.075 4.1525 3.075 4C3.075 3.8475 3.01442 3.70125 2.90659 3.59341C2.79875 3.48558 2.6525 3.425 2.5 3.425H1C0.8475 3.425 0.701247 3.48558 0.593414 3.59341C0.48558 3.70125 0.425 3.8475 0.425 4C0.425 4.1525 0.48558 4.29875 0.593414 4.40659C0.701247 4.51442 0.8475 4.575 1 4.575ZM8 8.925C7.8475 8.925 7.70125 8.98558 7.59341 9.09341C7.48558 9.20125 7.425 9.3475 7.425 9.5V11C7.425 11.1525 7.48558 11.2988 7.59341 11.4066C7.70125 11.5144 7.8475 11.575 8 11.575C8.1525 11.575 8.29875 11.5144 8.40659 11.4066C8.51442 11.2988 8.575 11.1525 8.575 11V9.5C8.575 9.3475 8.51442 9.20125 8.40659 9.09341C8.29875 8.98558 8.1525 8.925 8 8.925ZM4 3.075C4.1525 3.075 4.29875 3.01442 4.40659 2.90659C4.51442 2.79875 4.575 2.6525 4.575 2.5V1C4.575 0.8475 4.51442 0.701247 4.40659 0.593414C4.29875 0.48558 4.1525 0.425 4 0.425C3.8475 0.425 3.70125 0.48558 3.59341 0.593414C3.48558 0.701247 3.425 0.8475 3.425 1V2.5C3.425 2.6525 3.48558 2.79875 3.59341 2.90659C3.70125 3.01442 3.8475 3.075 4 3.075Z'
        fill='#FDFDFC'
        stroke='#FDFDFC'
        strokeWidth='0.15'
      />
    </svg>
  )
}

const LowBatteryIcon = () => {
  return (
    <svg width='10' height='16' viewBox='0 0 10 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4.425 6V8.5C4.425 8.6525 4.48558 8.79875 4.59341 8.90659C4.70125 9.01442 4.8475 9.075 5 9.075C5.1525 9.075 5.29875 9.01442 5.40659 8.90659C5.51442 8.79875 5.575 8.6525 5.575 8.5V6C5.575 5.8475 5.51442 5.70125 5.40659 5.59341C5.29875 5.48558 5.1525 5.425 5 5.425C4.8475 5.425 4.70125 5.48558 4.59341 5.59341C4.48558 5.70125 4.425 5.8475 4.425 6ZM5 9.925C4.83683 9.925 4.67733 9.97339 4.54165 10.064C4.40598 10.1547 4.30024 10.2835 4.2378 10.4343C4.17536 10.585 4.15902 10.7509 4.19085 10.9109C4.22268 11.071 4.30126 11.218 4.41664 11.3334C4.53201 11.4487 4.67902 11.5273 4.83905 11.5591C4.99908 11.591 5.16496 11.5746 5.31571 11.5122C5.46646 11.4498 5.59531 11.344 5.68596 11.2083C5.77661 11.0727 5.825 10.9132 5.825 10.75C5.825 10.5312 5.73808 10.3214 5.58336 10.1666C5.42865 10.0119 5.2188 9.925 5 9.925ZM3 1.075H7C7.1525 1.075 7.29875 1.01442 7.40659 0.906586C7.51442 0.798753 7.575 0.652499 7.575 0.5C7.575 0.347501 7.51442 0.201247 7.40659 0.0934136C7.29875 -0.0144199 7.1525 -0.075 7 -0.075H3C2.8475 -0.075 2.70125 -0.0144198 2.59341 0.0934136C2.48558 0.201247 2.425 0.3475 2.425 0.5C2.425 0.652499 2.48558 0.798753 2.59341 0.906586C2.70125 1.01442 2.8475 1.075 3 1.075ZM9.575 14V3.5C9.575 3.08228 9.40906 2.68168 9.11369 2.38631C8.81832 2.09094 8.41772 1.925 8 1.925H2C1.58228 1.925 1.18168 2.09094 0.886307 2.38631C0.590937 2.68168 0.425 3.08228 0.425 3.5V14C0.425 14.4177 0.590937 14.8183 0.886307 15.1137C1.18168 15.4091 1.58228 15.575 2 15.575H8C8.41772 15.575 8.81832 15.4091 9.11369 15.1137C9.40906 14.8183 9.575 14.4177 9.575 14ZM8.30052 3.19948C8.38022 3.27918 8.425 3.38728 8.425 3.5V14C8.425 14.1127 8.38022 14.2208 8.30052 14.3005C8.22082 14.3802 8.11272 14.425 8 14.425H2C1.88728 14.425 1.77918 14.3802 1.69948 14.3005C1.61978 14.2208 1.575 14.1127 1.575 14V3.5C1.575 3.38728 1.61978 3.27918 1.69948 3.19948C1.77918 3.11978 1.88728 3.075 2 3.075H8C8.11272 3.075 8.22082 3.11978 8.30052 3.19948Z'
        fill='#FDFDFC'
        stroke='#FDFDFC'
        strokeWidth='0.15'
      />
    </svg>
  )
}

const BandOfflineIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width={16} height={16} viewBox='0 0 16 16'>
    <path
      d='M10.676 11.11a.387.387 0 0 0 0-.555L10.125 10a.394.394 0 0 0-.555 0l-1.355 1.355c-.09.09-.277.09-.367 0L6.492 10a.394.394 0 0 0-.554 0l-.555.555a.394.394 0 0 0 0 .554l1.355 1.352a.28.28 0 0 1 0 .371l-1.355 1.352a.394.394 0 0 0 0 .554l.554.555a.394.394 0 0 0 .555 0l1.356-1.355c.09-.09.277-.09.367 0l1.355 1.355a.394.394 0 0 0 .555 0l.55-.555a.387.387 0 0 0 0-.554l-1.35-1.352a.28.28 0 0 1 0-.371ZM14.676 3.57C12.984 1.691 10.586.645 8.03.645 5.477.645 3.078 1.69 1.383 3.57c-.121.121-.09.336.031.43l.926.8c.12.122.305.09.43-.03a7.221 7.221 0 0 1 10.523 0 .333.333 0 0 0 .43.03l.922-.8c.125-.125.156-.309.03-.43ZM8.031 4.953a4.497 4.497 0 0 0-3.386 1.54c-.122.124-.09.339.03.46l.985.738c.125.094.309.094.402-.03.524-.552 1.231-.86 1.97-.86.738 0 1.445.308 1.937.828.094.125.277.125.402.031l.984-.738c.153-.121.153-.305.028-.461A4.446 4.446 0 0 0 8.03 4.953Zm0 0'
      style={{
        stroke: 'none',
        fillRule: 'nonzero',
        fill: '#FDFDFC',
        fillOpacity: 1,
      }}
    />
  </svg>
)

const HeartRateOfflineIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width={17} height={17} viewBox='0 0 17 17'>
    <path
      d='M4.792 5.002a4.628 4.628 0 0 0-.474.458c-.42.463-.75 1.009-.976 1.615A5.428 5.428 0 0 0 3 8.978c0 .656.116 1.301.342 1.908.226.601.557 1.153.976 1.616l6.199 6.855a1.994 1.994 0 0 0 2.966 0l2.686-2.973M3 3l15.998 15.998M9.22 4.379a4.48 4.48 0 0 1 1.462 1.08c.706.778 1.93.778 2.636 0C14.162 4.523 15.308 4 16.5 4c1.191 0 2.338.523 3.182 1.46.844.933 1.318 2.2 1.318 3.519 0 1.323-.474 2.586-1.318 3.524l-.904.998'
      style={{
        fill: 'none',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        stroke: '#FDFDFC',
        strokeOpacity: 1,
        strokeMiterlimit: 4,
      }}
      transform='scale(.70833)'
    />
  </svg>
)
export default PodAlerts
